/**
 * Utility for detecting in-app browsers (Instagram, Facebook, etc.) and redirecting to system browsers
 */

/**
 * Detects if the user is in an in-app browser
 * @returns {boolean} True if the user is in an in-app browser
 */
export const isInAppBrowser = (): boolean => {
  if (typeof window === "undefined") return false;
  
  const userAgent = window?.navigator?.userAgent;
  
  // List of common in-app browser identifiers
  const inAppBrowsers = [
    "Instagram", 
    "FBAV", 
    "FBAN", 
    "Twitter", 
    "Pinterest", 
    "TikTok", 
    "Line", 
    "LinkedIn", 
    "KAKAOTALK"
  ];
  
  return inAppBrowsers.some(app => userAgent.includes(app));
};

/**
 * Gets the phone type based on the user agent
 * @returns {"android" | "ios" | null} The phone type or null if not a mobile device
 */
export const getPhoneType = (): "android" | "ios" | null => {
  if (typeof window === "undefined") return null;
  
  const userAgent = window?.navigator?.userAgent;
  
  if (/Android/i.test(userAgent)) {
    return "android";
  } else if (/iPhone|iPad|iPod/i.test(userAgent)) {
    return "ios";
  }
  
  return null;
};

/**
 * Redirects to the system browser if the user is in an in-app browser
 * @param {number} delay - Delay in milliseconds before redirecting
 * @returns {boolean} True if redirection was attempted
 */
export const redirectToSystemBrowser = (delay: number = 0): boolean => {
  if (typeof window === "undefined") return false;
  
  // Check if URL contains redirected parameter to prevent infinite redirect loop
  const url = new URL(window?.location?.href);
  if (url?.searchParams?.get("redirected") === "true") {
    // Remove the redirected parameter from the URL
    url?.searchParams?.delete("redirected");
    window?.history?.replaceState({}, "", url.toString());
    return false;
  }
  
  const inApp = isInAppBrowser();
  const phoneType = getPhoneType();
  
  if (inApp && phoneType) {
    setTimeout(() => {
      const baseUrl = window?.location?.href.split('?')[0]; // Get base URL without query params
      const redirectUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}redirected=true`;
      
      if (phoneType === "android") {
        // Open in Chrome browser
        if (window?.location) { window.location.href = `intent:${redirectUrl}#Intent;scheme=https;package=com.android.chrome;end`; }
      } else if (phoneType === "ios") {
        // Open in Safari browser
        if (window?.location) { window.location.href = `x-safari-${redirectUrl}`; }
      }
    }, delay);
    
    return true;
  }
  
  return false;
};